/**
 * <search-bar> — header search overlay with predictive results, recent
 * searches, and client suggested terms. Query suggestions while typing come from
 * Search & Discovery via the predictive search API.
 */

import { Delegate } from '@theme/delegate';
import { responsive } from '@theme/responsive';
import { bodyScrollLockOptions } from '@theme/body-scroll-lock-options';
import { disableBodyScroll, enableBodyScroll } from '@theme/body-scroll-lock';

class SearchBar extends HTMLElement {
  #delegateElement = null;
  #delegateBody = null;
  #searchInputElement = null;
  #searchFormElement = null;
  #boundOnFormSubmit = null;
  #searchResultsElement = null;
  #drawerElement = null;
  #pageOverlayElement = null;
  #initialSearchLayout = null;
  #debouncedInput = null;
  #abortController = null;
  #closeListener = null;
  #boundOnKeyDown = null; 
  #isOpen = false;
  #lastInputValue = '';
  // Monotonic id per input/tap. iOS/WebKit doesn't reliably abort in-flight
  // fetches, so a stale predictive/count response can resolve late and overwrite
  // the current query's UI. Every async result checks this before touching the DOM.
  #requestId = 0;
  #options = {};
  #startsEmpty = false;
  #onDrawerOpening = (event) => {
    const { drawerId, isChild } = event.detail || {};
    if (drawerId === this.#drawerElement?.id || isChild) return;
    if (this.#isOpen) this.#closeSearch();
  };

  connectedCallback() {
    if (this.#delegateElement) {
      return;
    }

    this.#delegateElement = new Delegate(this);
    this.#delegateBody = new Delegate(document.body);
    this.#searchInputElement = this.querySelector('[name="q"]');
    this.#searchFormElement = this.querySelector('.searchForm');
    this.#searchResultsElement = this.querySelector('.searchWrapper');
    this.#drawerElement = this.querySelector('#quickSearchDrawer');
    this.#pageOverlayElement = this.querySelector('.sectionOverlay');
    this.#initialSearchLayout = this.#searchResultsElement?.cloneNode(true);
    this.#startsEmpty = this.classList.contains('search--empty');
    this.#options = this.#parseOptions();
    this.#applyAnimationSettings();

    if (this.#searchInputElement) {
      document.documentElement.style.setProperty(
        '--search-header-height',
        `${this.#searchInputElement.offsetHeight + 32}px`
      );
    }

    if (window.theme.showRecentSearches) {
      this.#getRecentSearches();
    }

    this.#bindEvents();
  }


  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;

    if (this.#closeListener && this.#pageOverlayElement) {
      this.#pageOverlayElement.removeEventListener('click', this.#closeListener);
    }

    if (this.#boundOnKeyDown) {
      document.removeEventListener('keydown', this.#boundOnKeyDown);
    }

    document.removeEventListener('drawer:opening', this.#onDrawerOpening);

    this.#delegateElement?.off();
    this.#delegateBody?.off();
    this.#searchInputElement?.removeEventListener('input', this.#debouncedInput);
    this.#searchFormElement?.removeEventListener('submit', this.#boundOnFormSubmit);

    this.#delegateElement = null;
    this.#delegateBody = null;
    this.#debouncedInput = null;
    this.#boundOnKeyDown = null;
    this.#boundOnFormSubmit = null;
    this.#closeListener = null;
  }

  /**
   * Full search page and View all must use the same query. Storefront search
   * ignores 1–2 character trailing terms unless we append `*`.
   */
  #normalizeSearchQuery(raw) {
    const value = String(raw ?? '').trim();
    if (!value || value.endsWith('*')) {
      return value;
    }

    const lastTerm = value.split(/\s+/).pop() ?? '';
    if (lastTerm.length > 0 && lastTerm.length < 3) {
      return `${value}*`;
    }

    return value;
  }

  #buildSearchPageUrl(rawQuery) {
    const form = this.#searchFormElement;
    const input = this.#searchInputElement;
    const query = this.#normalizeSearchQuery(rawQuery);
    if (!query || !form) {
      return null;
    }

    const params = new URLSearchParams();
    params.set(input?.name || 'q', query);
    params.set('options[prefix]', 'last');

    return `${form.action}?${params}`;
  }

  #onFormSubmit(event) {
    event.preventDefault();
    const url = this.#buildSearchPageUrl(this.#searchInputElement?.value);
    if (url) {
      window.location.assign(url);
    }
  }

  #syncViewAllHrefs() {
    const resultsUrl = this.#buildSearchPageUrl(this.#lastInputValue);
    if (!resultsUrl || !this.#searchResultsElement) {
      return;
    }

    this.#searchResultsElement.querySelectorAll('[data-search-view-all]').forEach((link) => {
      link.setAttribute('href', resultsUrl);
    });
  }

  #onCloseTouchEnd = (event) => {
    if (!this.#isOpen || event.touches.length > 0) {
      return;
    }
    this.#closeSearch(event);
  };

  #onViewAllClick = (event) => {
    const link = event.target.closest('[data-search-view-all]');
    if (!link || !this.contains(link)) {
      return;
    }

    const url = this.#buildSearchPageUrl(this.#lastInputValue || this.#searchInputElement?.value);
    if (!url) {
      return;
    }

    event.preventDefault();
    window.location.assign(url);
  };

  #setViewAllLabel(link, count) {
    const template =
      window.languages?.view_all_results || 'View all results (__COUNT__)';
    const label = template.replace('__COUNT__', String(count));
    const textNodes = link.querySelectorAll('.button-txt');
    if (textNodes.length > 0) {
      textNodes.forEach((node) => {
        node.textContent = label;
      });
      return;
    }
    link.textContent = label;
  }

  async #refreshViewAllMeta(signal, requestId) {
    const form = this.#searchFormElement;
    const query = this.#normalizeSearchQuery(this.#lastInputValue);
    if (!form || !query || !this.#searchResultsElement) {
      return;
    }

    this.#syncViewAllHrefs();

    const viewAllLinks = [
      ...this.#searchResultsElement.querySelectorAll('[data-search-view-all]'),
    ];

    const countUrl = new URL(form.action, window.location.origin);
    countUrl.searchParams.set('q', query);
    countUrl.searchParams.set('options[prefix]', 'last');
    countUrl.searchParams.set('type', 'product');
    countUrl.searchParams.set('section_id', 'search-predictive-count');

    try {
      const response = await fetch(countUrl.toString(), { signal });
      if (!response.ok) {
        return;
      }

      const html = await response.text();
      // Stale count from a previous query — do not overwrite the current label.
      if (requestId !== this.#requestId) {
        return;
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const countEl = doc.querySelector('[data-search-total-count]');
      const total = Number.parseInt(
        countEl?.getAttribute('data-search-total-count') || countEl?.textContent || '',
        10,
      );

      if (!Number.isFinite(total) || total < 1) {
        return;
      }

      viewAllLinks.forEach((link) => {
        this.#setViewAllLabel(link, total);
      });
    } catch (error) {
      if (error?.name === 'AbortError') {
        return;
      }
      throw error;
    }
  }

  /**
   * Predictive returned no products. Ask the full search engine (same query as
   * the results page) and, if it has matches, render them into the dropdown so
   * it never shows "no results" while the page does. If the page is also empty,
   * the predictive no-results state is left untouched.
   */
  async #applyFullSearchFallback(signal, requestId) {
    const form = this.#searchFormElement;
    const query = this.#normalizeSearchQuery(this.#lastInputValue);
    if (!form || !query || !this.#searchResultsElement) {
      return;
    }

    const fallbackUrl = new URL(form.action, window.location.origin);
    fallbackUrl.searchParams.set('q', query);
    fallbackUrl.searchParams.set('options[prefix]', 'last');
    fallbackUrl.searchParams.set('type', 'product');
    fallbackUrl.searchParams.set('section_id', 'search-predictive-fallback');

    const response = await fetch(fallbackUrl.toString(), { signal });
    if (!response.ok) {
      return;
    }

    const html = await response.text();
    // Stale response — a newer query is active; don't render this over it.
    if (requestId !== this.#requestId) {
      return;
    }

    const fallbackNode = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('#shopify-section-search-predictive-fallback');

    // No products from the full search either → keep the genuine no-results view.
    if (!fallbackNode?.querySelector('[data-search-fallback]')) {
      return;
    }

    this.#searchResultsElement.innerHTML = fallbackNode.innerHTML;
    this.#syncViewAllHrefs();

    const countEl = this.#searchResultsElement.querySelector('[data-search-total-count]');
    const total = Number.parseInt(countEl?.getAttribute('data-search-total-count') || '', 10);
    if (Number.isFinite(total) && total > 0) {
      this.#searchResultsElement
        .querySelectorAll('[data-search-view-all]')
        .forEach((link) => this.#setViewAllLabel(link, total));
    }
  }

  #bindEvents() {
    this.#debouncedInput = this.#debounce(this.#onInput.bind(this), 450);

    this.#delegateBody.on('click', '[data-action="open-search"]', this.#openSearch.bind(this));
    this.#delegateElement.on('click', '[data-action="close-search"]', this.#closeSearch.bind(this));
    // iOS spends the first tap outside the focused field on hiding the keyboard: the
    // viewport resizes under the finger and the synthetic click never arrives, so the x
    // needed two taps (869f4t65m). Closing on touchend does not wait for that click, and
    // cancelling it keeps the late click off the header icon sitting under the x.
    this.#delegateElement.on('touchend', '[data-action="close-search"]', this.#onCloseTouchEnd);
    this.#delegateElement.on('click', '[data-action="search-string"]', this.#onSuggestionActivate.bind(this));
    this.#delegateElement.on('keypress', '[data-action="search-string"]', this.#onSuggestionKeypress.bind(this));
    this.#delegateElement.on('click', '[data-search-view-all]', this.#onViewAllClick);
    this.#delegateBody.on('menu:open', this.#closeSearch.bind(this));
    this.#delegateBody.on('close:search', this.#closeSearch.bind(this));

    this.#searchInputElement?.addEventListener('input', this.#debouncedInput);

    // delegate-exempt: native form submit must fire on the actual <form>
    this.#boundOnFormSubmit = this.#onFormSubmit.bind(this);
    this.#searchFormElement?.addEventListener('submit', this.#boundOnFormSubmit);

    // delegate-exempt: Escape must close overlay while input is focused
    this.#boundOnKeyDown = (event) => {
      if (event.key === 'Escape' && this.#isOpen) {
        this.#closeSearch(event);
      }
    };
    document.addEventListener('keydown', this.#boundOnKeyDown);
    document.addEventListener('drawer:opening', this.#onDrawerOpening);
  }

  #parseOptions() {
    try {
      return JSON.parse(this.getAttribute('data-settings') || '{}');
    } catch {
      return {};
    }
  }

  #applyAnimationSettings() {
    // Mobile quick search must open/close instantly (no slide).
    this.style.setProperty('--search-mobile-animation-duration', '0s');
    this.classList.add('search--mobile-animation-disabled');
    this.#syncOverlayAnimation();
  }

  #syncOverlayAnimation() {
    if (!this.#pageOverlayElement) return;
    this.#pageOverlayElement.style.transition = 'none';
  }

  #isMobileDrawerMode() {
    return !responsive.matchesBreakpoint('lap_and_up');
  }

  #debounce(fn, delay) {
    let timer = null;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  #openSearch(event) {
    event.preventDefault();

    if (this.#isOpen) {
      this.#closeSearch(event);
      return;
    }

    document.dispatchEvent(
      new CustomEvent('search:opening', {
        bubbles: true,
        detail: { id: 'quickSearch' },
      })
    );

    this.style.display = 'block';
    this.dispatchEvent(new CustomEvent('menu:close', { bubbles: true }));

    this.setAttribute('aria-hidden', 'false');
    if (this.#lastInputValue === '') {
      this.classList.remove('search--results-view');
    }
    if (this.#isMobileDrawerMode()) {
      this.#drawerElement?.open?.();
    } else {
      if (this.#drawerElement) {
        this.#drawerElement.style.display = '';
        this.#drawerElement.removeAttribute('inert');
        this.#drawerElement.setAttribute('aria-hidden', 'false');
      }
      document.body.classList.add('scrollLock');
      this.#pageOverlayElement?.classList.add('is-visible');
    }

    // Focus synchronously, still inside the opening click's task: iOS/WebKit only
    // raises the keyboard for a focus() that runs within the user gesture, so the
    // rAF below left the field focused but the keyboard closed (869ejywrq). The rAF
    // stays as a correction — the drawer's focus trap focuses its first control
    // (the submit glyph) on open, and re-focusing an already-focused input is a
    // no-op everywhere else.
    this.#searchInputElement?.focus();
    requestAnimationFrame(() => {
      this.#searchInputElement?.focus();
    });

    this.#isOpen = true;

    if (this.#pageOverlayElement && !this.#isMobileDrawerMode()) {
      this.#closeListener = this.#closeSearch.bind(this);
      this.#pageOverlayElement.addEventListener('click', this.#closeListener);
    }
  }

  #closeSearch(event) {
    event?.preventDefault();

    if (!this.#isOpen) {
      return;
    }

    const finalizeClose = () => {
      if (!this.#isMobileDrawerMode()) {
        document.body.classList.remove('scrollLock');
      }
      this.setAttribute('aria-hidden', 'true');
      if (this.#drawerElement) {
        this.#drawerElement.setAttribute('aria-hidden', 'true');
        this.#drawerElement.setAttribute('inert', '');
        this.#drawerElement.style.display = 'none';
      }
      this.style.display = 'none';
      document.dispatchEvent(new CustomEvent('search:closed'));
    };

    if (this.#isMobileDrawerMode()) {
      // Instant close: finalize immediately. DrawerComponent would otherwise wait
      // for a slide transition / 450ms fallback even though search has no animation.
      this.#drawerElement?.close?.();
    }
    finalizeClose();
    if (!this.#isMobileDrawerMode()) {
      this.#pageOverlayElement?.classList.remove('is-visible');
    }
    this.#isOpen = false;
    // Every open starts from the empty state (869ead1xq). Reset after finalizeClose so
    // the panel is already hidden when its markup is swapped back — no flash of the
    // initial layout on the way out. Submit and view-all navigate away without going
    // through here, so the query they carry is never wiped mid-flight.
    this.#resetSearch();
  }

  #onSuggestionActivate(event) {
    const item = event.target.closest('[data-action="search-string"]');
    const value = item?.dataset.value;

    if (!value || !this.#searchInputElement) {
      return;
    }

    this.#searchInputElement.value = value;
    this.#onInput({ target: this.#searchInputElement });
  }

  #onSuggestionKeypress(event) {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    this.#onSuggestionActivate(event);
  }

  async #onInput(event) {
    this.#lastInputValue = event.target.value?.trim() ?? '';
    this.#searchResultsElement = this.querySelector('.searchWrapper');

    if (!this.#searchResultsElement) {
      return;
    }


    if (this.#lastInputValue === '') {
      this.classList.remove('search--results-view');
      this.#resetSearch();
      return;
    }

    this.classList.remove('search--empty');
    this.classList.add('search--results-view');

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const requestId = ++this.#requestId;

    try {
      const fetchUrl = new URL(window.routes.predictive_search_url, window.location.origin);
      fetchUrl.searchParams.set('q', this.#lastInputValue);
      fetchUrl.searchParams.set('resources[type]', 'query,product');
      fetchUrl.searchParams.set('resources[limit]', '10');
      fetchUrl.searchParams.set('resources[limit_scope]', 'each');
      fetchUrl.searchParams.set('section_id', 'search-predictive');

      const response = await fetch(fetchUrl.toString(), { signal });

      if (!response.ok) {
        throw new Error(String(response.status));
      }

      const text = await response.text();
      // A newer input/tap started while this was in flight — drop the stale
      // response instead of rendering it over the current query (iOS abort is
      // unreliable, so we can't trust the AbortController alone here).
      if (requestId !== this.#requestId) {
        return;
      }

      const sectionNode = new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector('#shopify-section-search-predictive');

      if (!sectionNode) {
        return;
      }

      this.#searchResultsElement.innerHTML = sectionNode.innerHTML;

      // Predictive search can't see product descriptions/metafields, so it may
      // return 0 products while the full results page has matches. In that case
      // show the page's products instead of a misleading "no results" (reuses a
      // /search section fetch, so the normal path stays a single count fetch).
      if (this.#searchResultsElement.querySelector('.predictiveSearch--noResults')) {
        await this.#applyFullSearchFallback(signal, requestId);
        if (requestId !== this.#requestId) {
          return;
        }
      } else {
        this.#syncViewAllHrefs();
        void this.#refreshViewAllMeta(signal, requestId);
      }

      if (window.theme.showRecentSearches) {
        this.#saveRecentSearch();
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      throw error;
    }
  }

  #resetSearch() {
    if (!this.#initialSearchLayout || !this.#searchResultsElement) {
      return;
    }

    // Invalidate any in-flight response so it can't repopulate the cleared box.
    this.#requestId++;
    this.#abortController?.abort();
    this.#searchResultsElement.innerHTML = this.#initialSearchLayout.innerHTML;

    if (this.#searchInputElement) {
      this.#searchInputElement.value = '';
      this.#searchInputElement.removeAttribute('value');
    }

    this.#lastInputValue = '';
    this.classList.remove('search--results-view');

    if (window.theme.showRecentSearches) {
      this.#getRecentSearches();
    }

    const recentWrapper = this.querySelector('[data-search-recent]');
    const hasRecentItems = recentWrapper && !recentWrapper.classList.contains('is-hidden');
    if (this.#startsEmpty && !hasRecentItems) {
      this.classList.add('search--empty');
    } else {
      this.classList.remove('search--empty');
    }

  }

  #saveRecentSearch() {
    if (this.#lastInputValue.length <= 3) {
      return;
    }

    const searchTerms = JSON.parse(localStorage.getItem('searchTerms') || '[]');
    if (!searchTerms.includes(this.#lastInputValue)) {
      searchTerms.unshift(this.#lastInputValue);
    }

    try {
      localStorage.setItem('searchTerms', JSON.stringify(searchTerms.slice(0, 5)));
    } catch {
      /* ignore */
    }
  }

  #getRecentSearches() {
    const items = JSON.parse(localStorage.getItem('searchTerms') || '[]');
    const wrapper = this.querySelector('[data-search-recent]');
    const list = wrapper?.querySelector('.search--recentResults');
    const initialSuggestions = this.querySelector('[data-search-initial-suggestions]');

    if (!list || !wrapper) {
      return;
    }

    if (initialSuggestions) {
      // Suggested terms take priority over recent searches in initial view.
      wrapper.classList.add('is-hidden');
      return;
    }

    if (items.length === 0) {
      wrapper.classList.add('is-hidden');
      list.innerHTML = '';
      return;
    }

    wrapper.classList.remove('is-hidden');
    list.innerHTML = items
      .map(
        (term) =>
          `<li role="option" aria-selected="false" tabindex="0" data-action="search-string" data-value="${this.#escapeHtml(term)}"><p class="u-p3 searchSuggestion">${this.#escapeHtml(term)}</p></li>`
      )
      .join('');
  }

  #escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

if (!customElements.get('search-bar')) {
  customElements.define('search-bar', SearchBar);
}
